import Button from "components/ui/Button";
import PasswordInput from "components/ui/PasswordInput";
import TextInput from "components/ui/TextInput";
import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import ArrowLeft from "images/arrow_left.svg?react";
import { routes } from "pages/routes";
import { ChangeEvent, FocusEvent, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLocationState } from "utils/routing";
import {
  isAlreadyActiveResendError,
  logIn,
  resendVerificationToken,
  resendVerifyMessage,
  signInErrorCode,
  signInMfa,
} from "services/users/auth";
import type { SignInErrorCode } from "services/users/auth";
import { ApiRequestError, HTTP_CODES } from "services/errors";
import type { ErrorMessages } from "services/errors";
import ApiErrorBanner from "components/ApiErrorBanner";
import ErrorBanner from "components/ui/ErrorBanner";
import { getSuccessComponents } from "services/util";

import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import assert from "utils/assert";
import { SignInLocationState } from "./SignIn";
import styles from "./SignIn.module.scss";
import useSignInForm from "./utils";
import { trackEvent } from "services/productAnalytics";

type Errors = string | Error;

const SUPPORT_EMAIL = "scheduling@talaera.com";

type Props = {
  setMfaMode: (mode: boolean) => void;
};

export default function EmailSignInForm({ setMfaMode }: Props) {
  const navigate = useNavigate();
  const { refreshCurrentUser } = useRequiredContext(UserContext);
  const { t } = useTranslation();
  const location = useLocation();
  const locationState = useLocationState<SignInLocationState>();

  const [resetDone, setResetDone] = useState(locationState?.resetDone ?? false);

  const [mfaToken, setMfaToken] = useState(locationState?.mfaToken);

  const signIn = useMutation({ mutationFn: logIn });
  const mfa = useMutation({ mutationFn: signInMfa });

  const initialData = {
    email: locationState?.email ?? "",
    password: mfaToken ? undefined : "",
    code: "",
  };

  const {
    account,
    setAccount,
    errors: formErrors,
    touched,
    setTouched,
    onValidate,
    onTouched,
  } = useSignInForm(initialData, !!mfaToken);

  const codeRef = useRef<HTMLInputElement>(null);

  const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (mfaToken) {
      void handleMfaSubmit();
    } else {
      void handleSignIn();
    }
  };

  const handleSignIn = async () => {
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
    });

    if (isValidated) {
      setResetDone(false);
      scrubPassedError();
      assert(account.email && account.password, "Expected email and password to be set");
      trackEvent("email_sign_in_submitted");
      try {
        const resp = await signIn.mutateAsync({
          email: account.email,
          passwd: account.password,
        });

        if (resp.mfa) {
          setMfaToken(resp.token);
          setMfaMode(true);
          codeRef.current?.focus();
        } else {
          await onSignInSuccess();
        }
      } catch (error) {
        reportSignInError(error);
        if (signInErrorCode(error) === "sso_required") {
          // SSO-managed account without a password: the SSO sign-in is the only working
          // path. Clearing the refusal keeps it off screen while the redirect lands.
          signIn.reset();
          const query = new URLSearchParams({ email: account.email ?? "" });
          navigate(`${routes.public.signInSso.path()}?${query}`, {
            replace: true,
          });
        }
      }
    }
  };

  const handleMfaSubmit = async () => {
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
      code: true,
    });

    if (isValidated) {
      scrubPassedError();
      try {
        await mfa.mutateAsync({
          email: account.email as string,
          token: mfaToken as string,
          code: account.code as string,
        });
        await onSignInSuccess();
      } catch (error) {
        reportSignInError(error);
      }
    }
  };

  const onSignInSuccess = async () => {
    await refreshCurrentUser();

    trackEvent("sign_in_success");

    const redirectPath = locationState?.returnPath
      ? locationState.returnPath
      : routes.root.path() + location.hash;

    navigate(redirectPath, {
      replace: true,
    });
  };

  const handleBack = () => {
    setMfaMode(false);
    scrubPassedError();
    mfa.reset();
    setMfaToken(undefined);
  };

  // The browser persists router state in history.state, so an error left there comes
  // back on every reload. Drop it once the user acts, keeping the rest of the state:
  // `returnPath` is read at sign-in success and forwarded to the OAuth and SSO pages.
  const scrubPassedError = () => {
    if (locationState?.error) {
      const { error: _dropped, ...rest } = locationState;
      navigate(location.pathname, { replace: true, state: rest });
    }
  };

  // Only one attempt can be in error at a time: submitting either form clears its own
  // result, and going back from the code step clears the MFA one.
  const failed = signIn.error ? signIn : mfa.error ? mfa : undefined;
  const error: Errors | undefined = failed?.error ?? locationState?.error;

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {error && (
        <div className={styles.error}>
          <SignInError error={error} email={failed?.variables.email} isMfa={!!mfaToken} />
        </div>
      )}

      {resetDone && (
        <div className={styles.success}>
          {getSuccessComponents(t("Auth.emailForm.resetPasswordSuccess"))}
        </div>
      )}

      <TextInput
        label={t("Auth.emailForm.emailLabel")}
        error={touched.email && formErrors.email}
        size="large"
        name="email"
        placeholder="johnsmith@gmail.com"
        value={account.email}
        onChange={handleChangeField}
        onBlur={handleBlurField}
        disabled={!!mfaToken}
        invalid={!!(formErrors.email && touched.email)}
      />

      {mfaToken ? (
        <>
          <TextInput
            label={t("Auth.mfaForm.codeLabel")}
            error={touched.code && formErrors.code}
            size="large"
            className={styles.codeInput}
            name="code"
            type="number"
            placeholder={t("Auth.mfaForm.codePlaceholder")}
            value={account.code}
            onChange={handleChangeField}
            onBlur={handleBlurField}
            ref={codeRef}
            invalid={!!(formErrors.code && touched.code)}
          />

          <Button
            className={styles.button}
            size="large"
            type="submit"
            loading={mfa.isPending}
          >
            {t("Auth.mfaForm.submit")}
          </Button>

          <button
            className={classNames(styles.link, styles.backButton)}
            type="button"
            onClick={handleBack}
          >
            <ArrowLeft />
            {t("Common.actions.back")}
          </button>
        </>
      ) : (
        <>
          <PasswordInput
            label={t("Auth.emailSignup.passwordLabel")}
            error={touched.password && formErrors.password}
            size="large"
            name="password"
            autoComplete="current-password"
            placeholder={t("Auth.emailForm.passwordPlaceholder")}
            value={account.password}
            invalid={!!(formErrors.password && touched.password)}
            onChange={handleChangeField}
            onBlur={handleBlurField}
          />

          <div className={styles.forgotLink}>
            <Link className={styles.link} to={routes.public.forgot.path()}>
              {t("Auth.emailForm.forgotPassword")}
            </Link>
          </div>

          <Button
            className={styles.button}
            size="large"
            type="submit"
            loading={signIn.isPending}
          >
            {t("Auth.emailForm.submit")}
          </Button>
        </>
      )}
    </form>
  );
}

// The response body stays out of analytics: it can name the account and carries the
// resend token.
function reportSignInError(error: unknown) {
  const apiError = error instanceof ApiRequestError ? error : undefined;
  trackEvent("sign_in_error", {
    status: apiError?.response?.status,
    error_code: apiError?.errorCode,
  });
}

type SignInErrorProps = {
  error: Errors;
  // The address the refused attempt used, which the email field may no longer hold: it
  // stays editable while the message is on screen.
  email: string | undefined;
  isMfa: boolean;
};

// A refusal the endpoint labelled gets its own message and next step; anything else shows
// the text the server sent.
function SignInError({ error, email, isMfa }: SignInErrorProps) {
  const { t } = useTranslation();

  const supportLink = <a href={`mailto:${SUPPORT_EMAIL}`} />;
  const forgotLink = <Link className={styles.link} to={routes.public.forgot.path()} />;

  const accountDeleted = (
    <Trans i18nKey="Auth.emailForm.accountDeleted" components={{ a: supportLink }} />
  );

  const messages: ErrorMessages<SignInErrorCode> = {
    // Mid-MFA the password is already accepted, so this code means a stale token
    // rather than a mistyped password.
    invalid_credentials: isMfa ? (
      t("Auth.mfaForm.restart")
    ) : (
      <Trans i18nKey="Auth.emailForm.invalidCredentials" components={{ a: forgotLink }} />
    ),
    invalid_mfa_code: t("Auth.mfaForm.invalidCode"),
    account_pending: (
      <AccountPendingMessage email={email} token={resendVerificationToken(error)} />
    ),
    password_reset_pending: (
      <Trans
        i18nKey="Auth.emailForm.passwordResetPending"
        components={{ a: forgotLink }}
      />
    ),
    user_deleted: accountDeleted,
  };

  // A 410 says the account is gone whether or not the refusal carried a code, so it
  // answers on its own rather than through the code lookup.
  const isGone =
    error instanceof ApiRequestError && error.response?.status === HTTP_CODES.GONE;

  return isGone ? (
    <ErrorBanner>{accountDeleted}</ErrorBanner>
  ) : (
    <ApiErrorBanner error={error} messages={messages} />
  );
}

type AccountPendingMessageProps = {
  email: string | undefined;
  token: string | undefined;
};

function AccountPendingMessage({ email, token }: AccountPendingMessageProps) {
  const { t } = useTranslation();
  const resend = useMutation({
    mutationFn: (verificationToken: string) =>
      resendVerifyMessage({ resend_verification_token: verificationToken }),
  });

  const supportLink = <a href={`mailto:${SUPPORT_EMAIL}`} />;

  const alreadyActive = isAlreadyActiveResendError(resend.error);
  const canResend = !!token && !resend.isSuccess && !alreadyActive;

  const handleResend = () => {
    if (token) {
      resend.mutate(token);
    }
  };

  return (
    <>
      <Trans
        i18nKey="Auth.emailForm.accountPending"
        values={{ email: email ?? "" }}
        components={{ strong: <strong /> }}
      />{" "}
      {resend.isSuccess && t("Auth.emailForm.verificationResent")}
      {alreadyActive && t("Auth.emailForm.alreadyActivated")}
      {resend.isError && !alreadyActive && (
        <>
          <Trans
            i18nKey="Auth.emailForm.resendFailed"
            components={{ a: supportLink }}
          />{" "}
        </>
      )}
      {canResend && (
        <Button variant="plain" loading={resend.isPending} onClick={handleResend}>
          {t("Auth.emailForm.resendVerification")}
        </Button>
      )}
    </>
  );
}
